import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useVsiAppContext } from "../../../stores/VsiApp";
import { useDataWithRoute, useDebouncedCallback, useTitleFromComponent, useUrlState } from "../../../utils/hooks";
import Stockchart from "../../../components/Stockchart";
import { VsiBubble, VsiH4, VsiSmallText } from "../../../styled/App.styled";
import { Card } from "reactstrap";
import Grid from "../../../components/Grid";
import VsiLabeledSelect from "../../../components/VsiLabeledSelect";
import { formatStockChartConfig } from "../../../utils/Helpers";
import { cloudTrends } from "../../../utils/gridColumnDefs/cloud/cloudTrends";
import { genericColors } from "../../../utils/chartUtils";

const cloudMetricOptions = [
  { value: "disk_used", label: "Disk Used" },
  { value: "total_disk", label: "Total Disk" },
  { value: "disk_mbps", label: "Disk Mbps" },
  { value: "disk_iops", label: "Disk Iops" },
  { value: "vcpu", label: "vCPU" },
  { value: "cpu_used", label: "CPU Used" },
  { value: "total_cpu", label: "Total CPU" },
  { value: "memory_used", label: "Memory Used" },
  { value: "free_memory", label: "Free Memory" },
  { value: "total_memory", label: "Total Memory" },
  { value: "iops_capacity", label: "Iops Capacity" },
  { value: "latency", label: "Latency" },
  { value: "total_cost", label: "Total Cost" }
];

const unitTypeConfigs = [
  { value: "all_cloud_units", label: "All Cloud Platforms", yAxis: "GiB" },
  { value: "by_cloud_units", label: "By Cloud Platform", yAxis: "GiB" }
];

const cloudUnits = [
  { value: "AMAZON_AWS", label: "AWS" },
  { value: "MICROSOFT_AZURE", label: "Azure" },
  { value: "GOOGLE_CLOUD_DEVICE", label: "Google Cloud" }
];

const overflowInitialPicks = 5;

export default function CloudTrends({ title, context }) {
  // hooks
  useTitleFromComponent(`${context} / ${title}`);
  const { theme, client, periodId } = useVsiAppContext();
  const [params, setParams] = useUrlState({
    metric: unitTypeConfigs[0].value,
    metric_type: cloudMetricOptions[0].value,
    cloud_unit: "",
    units: []
  });

  const { metric, metric_type, units, cloud_unit } = params;

  // state
  const [chartData, setChartData] = useState([]);
  const [noDataFound, setNoDataFound] = useState(false);
  const [isMunging, setIsMunging] = useState(true);
  const [cloudUnitOptions, setCloudUnitOptions] = useState([]);
  const [overflowTypeOptions, setOverflowTypeOptions] = useState([]);
  const [formattedChartData, setFormattedChartData] = useState([]);
  const [chartConfig, setChartConfig] = useState({
    colors: genericColors
  });

  const unitRoute = useMemo(() => {
    return metric === "all_cloud_units" ?
      `/api/v6/report/enterprise_trends_by_all_cloud_units_proc?client_id=${client.clientId}&period_id=${periodId}` :
      `/api/v6/report/enterprise_trends_by_cloud_units_proc?client_id=${client.clientId}&period_id=${periodId}&cloud_unit=${cloud_unit}`;
  }, [client, periodId, cloud_unit, metric]);

  const [{ recordsets }, { isLoading }] = useDataWithRoute(unitRoute);
  const [data, addtlData] = recordsets;

  // memos
  const activeMetric = useMemo(() => {
    return unitTypeConfigs.find(option => metric === option.value) || unitTypeConfigs[0];
  }, [metric]);

  const activeMetricType = useMemo(() => {
    return cloudMetricOptions.find(option => metric_type === option.value) || cloudMetricOptions[0];
  }, [metric_type]);

  const activeCloudUnit = useMemo(() => {
    return cloudUnitOptions.find(option => cloud_unit === option.value) || cloudUnitOptions?.[0] || {
      label: "Loading...",
      value: "Loading..."
    };
  }, [cloud_unit, cloudUnitOptions]);

  const activeUnits = useMemo(() => {
    return units.map(opt => ({ value: opt, label: opt }));
  }, [units]);

  const handleData = useCallback(async (data) => {
    const isDataFound = data.length === 0;
    const includeDollar = metric_type.includes("cost") ? "$" : "";
    // Aggregate by Subscription

    if (metric === "all_cloud_units" && cloudUnitOptions.length < 1) {
      // Remove empty cloudUnit selection on the all units check
      const allTypes = data.map((item) => item?.device_type_code);
      const uniqueTypes = Array.from(new Set(allTypes));
      const foundCloudUnits = cloudUnits.filter((unit) =>
        uniqueTypes.includes(unit.value)
      );
      setCloudUnitOptions(foundCloudUnits);
    } else if (metric === "by_cloud_units" && addtlData?.length > 0) {
      const newUnits = addtlData.map(unit => {
        return cloudUnits.find(opt => opt.value === unit.device_type_code);
      });
      setCloudUnitOptions(newUnits);
    }

    const uniqueSubscription = new Set();
    const dtNames = [];

    data.forEach((item) => {
      if (!uniqueSubscription.has(item?.["subscription"]) && units.some(opt => opt === item?.subscription)) {
        dtNames.push({
          name: item?.["subscription"],
          field: metric_type,
          dataLabels: { format: `${includeDollar}{point.y:,.0f}` },
          tooltip: { pointFormat: `{series.name}: <b>${includeDollar}{point.y:,.0f}</b>` }
        });
        uniqueSubscription.add(item?.["subscription"]);
      }
    });

    let config = formatStockChartConfig(data, dtNames, "subscription");

    setChartData(data);
    setNoDataFound(isDataFound);
    setFormattedChartData(config?.series);
    const titleText = cloudMetricOptions.find(opt => opt.value === metric_type).label;
    setChartConfig(config => {
      if (config?.yAxis?.[0]?.title === titleText) return config;
      return {
        yAxis: [
          {
            title: {
              text: cloudMetricOptions.find(opt => opt.value === metric_type).label
            }
          }
        ]
      };
    });
    setIsMunging(false);
  }, [data, addtlData, metric, cloud_unit, metric_type, units]);

  const overflowOptions = useMemo(() => {
    const overFlowOptions = [];
    const uniqueSubscription = new Set();
    data.forEach(datum => {
      if (!uniqueSubscription.has(datum?.["subscription"])) {
        uniqueSubscription.add(datum?.["subscription"]);
        overFlowOptions.push({
          value: datum?.subscription,
          label: datum?.subscription
        });
      }
    });
    return overFlowOptions;
  }, [data]);

  const debouncedHandleData = useDebouncedCallback(handleData, 250);

  useEffect(() => {
    if (!isLoading) {
      setIsMunging(true);
      debouncedHandleData(data);
    }
  }, [data, addtlData, metric, cloud_unit, metric_type, units, debouncedHandleData, isLoading]);

  useEffect(() => {
    if (overflowOptions.length > 0 && (units.length < 1 || !units.every(src => overflowOptions.some(tgt => src === tgt.value)))) {
      setParams({
        units: overflowOptions.slice(0, overflowInitialPicks).map(opt => opt.value)
      });
    }
  }, [overflowOptions, units, cloud_unit]);


  const onUnitTypeChange = ({ value }) => {
    setParams({
      metric: value
    });
  };

  const onMetricChange = async ({ value }) => {
    setParams({
      metric_type: value
    });
  };

  const onAdditionalOptionChange = async ({ value }) => {
    setParams({
      cloud_unit: value
    });
  };

  const onOverflowChange = (values) => {
    setParams({
      units: Array.from(new Set(values.map(opt => opt.label)))
    });
  };

  const filterChanged = useCallback((eventData) => {
    let filteredData = [];
    eventData.api.forEachNodeAfterFilter(node => {
      filteredData.push(node.data);
    });
    debouncedHandleData(filteredData);
  }, [debouncedHandleData]);

  return (
    <Card className={`report-card p-2 nav-card-${theme}`}>
      <VsiH4>{metric === "all_cloud_units" ? `${activeMetricType?.label} Trend By ${activeMetric?.label} ` :
        ` ${activeMetricType?.label} Trend By ${activeCloudUnit?.label} Platform `}</VsiH4>
      <VsiBubble noAutobot>
        <div className={"w-100 pb-2 d-flex"}>
          <VsiLabeledSelect options={unitTypeConfigs}
                          isLoading={isLoading || isMunging}
                            isDisabled={isLoading || isMunging}
                            label={"Chart Type"}
                            minWidth={200}
                            maxWidth={500}
                            onChange={onUnitTypeChange}
                            defaultValue={activeMetric} />
          {metric === "by_cloud_units" ? (
            <VsiLabeledSelect options={cloudUnitOptions}
                          isLoading={isLoading || isMunging}
                              isDisabled={isLoading || isMunging}
                              label={"Cloud Platform"}
                              minWidth={200}
                              maxWidth={600}
                              onChange={onAdditionalOptionChange}
                              defaultValue={activeCloudUnit} />
          ) : null}
          <VsiLabeledSelect options={cloudMetricOptions}
                          isLoading={isLoading || isMunging}
                            isDisabled={isLoading || isMunging}
                            label={"Metric Type"}
                            minWidth={225}
                            onChange={onMetricChange}
                            defaultValue={activeMetricType} />
        </div>
        <VsiLabeledSelect options={overflowOptions}
                          isLoading={isLoading || isMunging}
                          isDisabled={isLoading || isMunging}
                          label={"Subscriptions"}
                          minWidth={200}
                          maxWidth={"100%"}
                          onChange={onOverflowChange}
                          isMulti
                          defaultValue={activeUnits} />
        {noDataFound ? <VsiSmallText> No data found. </VsiSmallText> : null}
      </VsiBubble>

      <VsiBubble
        unit={metric_type}
        autobotInfo={{
          secondaryMetric: [metric, cloud_unit],
          yAxes: chartConfig.yAxis
        }}
      >
        <Stockchart series={formattedChartData} config={chartConfig} isLoading={isLoading || isMunging}/>
      </VsiBubble>
      <VsiBubble unit={metric_type}
                 autobotInfo={{ secondaryMetric: [metric, cloud_unit] }}>
        <Grid
          addStyle
          data={chartData}
          columnDefs={cloudTrends}
          saveParams={{
            type: metric_type,
            metric: metric
          }}
          filterChanged={filterChanged} />
      </VsiBubble>

    </Card>
  );
}
